import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  cancelRequests,
  fetchRequests,
} from "../../../../store/remediation/remediationSlice";
import { readFiltersFromUrl, writeFiltersToUrl } from "./filterUrlSync";
import FilterBar from "./FilterBar";
import EmptyState from "./EmptyState";
import StatusChip, { SeverityChip } from "../common/StatusChip";
import RequestDetailDrawer from "../RequestDetail";
import RescheduleModal from "../RescheduleModal";
import ConfirmDialog from "../common/ConfirmDialog";
import { NON_ACTIONABLE_STATUSES } from "../common/statusColors";
import { friendlyErrorMessage, friendlySkippedReason } from "../common/ErrorCodeMap";
import { useRemediationSnackbar } from "../common/SnackbarHost";
import {
  formatRelative,
  formatAbsolute,
  useActionFeedback,
  useDebouncedValue,
  usePollingInterval,
} from "../common/hooks";
import { remediationHubSx } from "../common/filterBarStyles";
import {
  formatRequestRef,
  getRequestRowSx,
  sortRequestsByColumn,
} from "../common/requestListHelpers";
import { useCloudAccounts } from "../common/useCloudAccounts";
import { buildRemediationHubUrl, clearRequestIdFromUrl, readRequestIdFromUrl } from "../common/remediationHubNavigation";

const DEFAULT_SORT = "urgency:desc";

/** Fields sent to the API `sort` param. All other header sorts are client-side. */
const API_SORT_FIELDS = new Set(["urgency", "check_title", "severity", "created_at"]);

const CLIENT_SORT_FIELDS = new Set([
  "id",
  "resource_arn",
  "account",
  "status",
  "scheduled_at",
  "created_by",
]);

/** Column → first-click direction. */
const COLUMN_SORT_INITIAL_DIR = {
  id: "asc",
  check_title: "asc",
  resource_arn: "asc",
  account: "asc",
  severity: "desc",
  status: "asc",
  scheduled_at: "asc",
  created_at: "desc",
  created_by: "asc",
};

const parseSort = (sort) => {
  const raw = String(sort || DEFAULT_SORT);
  const sep = raw.lastIndexOf(":");
  if (sep <= 0) return { field: raw, dir: "desc" };
  return {
    field: raw.slice(0, sep),
    dir: raw.slice(sep + 1) === "asc" ? "asc" : "desc",
  };
};

const nextColumnSort = (currentSort, field) => {
  const { field: activeField, dir } = parseSort(currentSort);
  if (activeField === field) return `${field}:${dir === "asc" ? "desc" : "asc"}`;
  return `${field}:${COLUMN_SORT_INITIAL_DIR[field] || "asc"}`;
};

const SortableHeader = ({ field, sort, onSort, children, sx }) => {
  const { field: activeField, dir } = parseSort(sort);
  const active = activeField === field;
  return (
    <TableCell
      sortDirection={active ? dir : false}
      sx={{ fontWeight: 600, ...sx }}
    >
      <TableSortLabel
        active={active}
        direction={active ? dir : COLUMN_SORT_INITIAL_DIR[field] || "asc"}
        onClick={() => onSort(field)}
        sx={{
          color: "inherit !important",
          "& .MuiTableSortLabel-icon": { fontSize: 14, color: "#94a3b8" },
          "&.Mui-active": { color: "inherit !important" },
        }}
      >
        {children}
      </TableSortLabel>
    </TableCell>
  );
};

const buildApiParams = (filters) => {
  const params = {};
  if (filters.status_in) params.status_in = filters.status_in;
  if (filters.severity) params.severity = filters.severity;
  if (filters.service) params.service_name = filters.service;
  if (filters.account) params.account_number = filters.account;
  if (filters.check) params.check_id = filters.check;
  if (filters.created_from) params.created_at_from = filters.created_from;
  if (filters.created_to) params.created_at_to = filters.created_to;
  if (filters.search) params.search = filters.search;
  if (filters.sort) {
    const { field } = parseSort(filters.sort);
    params.sort = API_SORT_FIELDS.has(field) ? filters.sort : DEFAULT_SORT;
  }
  if (filters.page_size) params.page_size = Number(filters.page_size);
  if (filters.next_token) params.next_token = filters.next_token;
  return params;
};

const SKELETON_ROW_COUNT = 8;

const deriveServiceOptions = (items) => {
  const serviceSet = new Set();
  for (const r of items || []) {
    if (r.service_name) serviceSet.add(r.service_name);
  }
  return Array.from(serviceSet).sort();
};

const RequestsList = ({ onGoToIamTab }) => {
  const dispatch = useDispatch();
  const { showSnackbar } = useRemediationSnackbar();
  const list = useSelector((s) => s.remediation.list);
  const actionInProgress = useSelector((s) => s.remediation.actionInProgress);

  const [filters, setFilters] = useState(() => ({
    sort: DEFAULT_SORT,
    page_size: 50,
    ...readFiltersFromUrl(),
  }));
  const [searchInput, setSearchInput] = useState(filters.search || "");
  const debouncedSearch = useDebouncedValue(searchInput, 500);
  const [selectedIds, setSelectedIds] = useState([]);
  const [tokenStack, setTokenStack] = useState([]); // prev-page cursors
  const [detailId, setDetailId] = useState(null);
  // The list slice's status flag is our source of truth for "refreshing".
  const refreshing = list.status === "loading";
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleIds, setRescheduleIds] = useState([]);
  const [bulkCancelOpen, setBulkCancelOpen] = useState(false);
  const cancelSubmitting = Boolean(actionInProgress?.cancel);
  const serviceOptionsCache = useRef([]);
  const { accountFilterOptions: accountOptions, loading: accountsLoading } =
    useCloudAccounts();

  // Apply debounced search to filters (waits until typing pauses).
  useEffect(() => {
    const q = (debouncedSearch || "").trim();
    if ((filters.search || "") !== q) {
      setFilters((f) => ({ ...f, search: q || null, next_token: null }));
      setTokenStack([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const applySearchNow = useCallback((term) => {
    const next = term !== undefined ? term : searchInput;
    const q = String(next).trim();
    setSearchInput(next);
    setFilters((f) => ({ ...f, search: q || null, next_token: null }));
    setTokenStack([]);
  }, [searchInput]);

  // Mirror filters to URL whenever they change.
  useEffect(() => {
    writeFiltersToUrl(filters);
  }, [filters]);

  // Fetch list on filter change.
  const apiParams = useMemo(() => buildApiParams(filters), [filters]);
  const apiParamsKey = JSON.stringify(apiParams);
  useEffect(() => {
    dispatch(fetchRequests(apiParams));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiParamsKey, dispatch]);

  // Polling timer driven by X-Next-Poll-Suggestion.
  const reloadCurrent = useCallback(
    (options = {}) => {
      dispatch(fetchRequests({ ...apiParams, ...options }));
    },
    [dispatch, apiParams]
  );

  const reloadSilent = useCallback(() => {
    reloadCurrent({ silent: true });
  }, [reloadCurrent]);
  usePollingInterval(reloadSilent, list.next_poll_seconds, true);

  const handleFilterChange = (patch) => {
    setFilters((f) => ({ ...f, ...patch, next_token: null }));
    setTokenStack([]);
    setSelectedIds([]);
  };

  const handleSortColumn = (field) => {
    const nextSort = nextColumnSort(filters.sort, field);
    const nextField = parseSort(nextSort).field;
    const currentField = parseSort(filters.sort).field;
    const staysOnClient =
      CLIENT_SORT_FIELDS.has(nextField) &&
      (CLIENT_SORT_FIELDS.has(currentField) || currentField === "urgency");
    if (staysOnClient) {
      setFilters((f) => ({ ...f, sort: nextSort }));
      return;
    }
    handleFilterChange({ sort: nextSort });
  };

  const handleResetFilters = () => {
    setFilters({ sort: DEFAULT_SORT, page_size: 50 });
    setSearchInput("");
    setTokenStack([]);
    setSelectedIds([]);
  };

  // Pagination
  const handleNextPage = () => {
    if (!list.next_token) return;
    setTokenStack((stk) => [...stk, filters.next_token || null]);
    setFilters((f) => ({ ...f, next_token: list.next_token }));
  };
  const handlePrevPage = () => {
    if (tokenStack.length === 0) return;
    const prev = tokenStack[tokenStack.length - 1];
    setTokenStack((stk) => stk.slice(0, -1));
    setFilters((f) => ({ ...f, next_token: prev }));
  };
  const handleFirstPage = () => {
    setTokenStack([]);
    setFilters((f) => ({ ...f, next_token: null }));
  };

  const displayItems = useMemo(() => {
    const items = Array.isArray(list.items) ? list.items : [];
    const { field, dir } = parseSort(filters.sort);
    if (!CLIENT_SORT_FIELDS.has(field)) return items;
    return sortRequestsByColumn(items, field, dir);
  }, [list.items, filters.sort]);

  const itemsById = useMemo(
    () => new Map(displayItems.map((r) => [r.id, r])),
    [displayItems]
  );

  const bulkActionableSelectedIds = useMemo(
    () =>
      selectedIds.filter((id) => {
        const status = itemsById.get(id)?.status;
        return status && !NON_ACTIONABLE_STATUSES.has(status);
      }),
    [selectedIds, itemsById]
  );

  const skippedBulkActionCount = selectedIds.length - bulkActionableSelectedIds.length;

  // Bulk row selection
  const toggleRow = (id) => {
    setSelectedIds((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]
    );
  };
  const toggleAll = (checked) => {
    setSelectedIds(checked ? displayItems.map((r) => r.id) : []);
  };

  // Bulk actions
  const handleBulkReschedule = () => {
    if (bulkActionableSelectedIds.length === 0) return;
    setRescheduleIds(bulkActionableSelectedIds);
    setRescheduleOpen(true);
  };

  useActionFeedback("cancel", ({ succeeded = [], skipped = [], error }) => {
    if (error) {
      showSnackbar({ message: friendlyErrorMessage(error), severity: "error" });
      return;
    }
    if (succeeded.length && skipped.length === 0) {
      showSnackbar({
        message: `Cancelled ${succeeded.length} task${succeeded.length === 1 ? "" : "s"}.`,
        severity: "success",
      });
    } else if (succeeded.length && skipped.length) {
      showSnackbar({
        message: `Cancelled ${succeeded.length}, skipped ${skipped.length}.`,
        severity: "warning",
        title: "Partial success",
        details: skipped.map((s) => `#${s.request_id}: ${friendlySkippedReason(s)}`),
      });
    } else {
      showSnackbar({
        message: "No tasks were cancelled.",
        severity: "warning",
        title: "All skipped",
        details: skipped.map((s) => `#${s.request_id}: ${friendlySkippedReason(s)}`),
      });
    }
    setBulkCancelOpen(false);
    setSelectedIds([]);
    reloadCurrent();
  });

  const liveServiceOptions = useMemo(
    () => deriveServiceOptions(list.items),
    [list.items]
  );

  // Keep every service seen this session. A service filter returns only that
  // service's rows, so replacing the cache with the live page would hide the rest.
  useEffect(() => {
    if (list.status !== "succeeded" || liveServiceOptions.length === 0) return;
    const merged = new Set([...serviceOptionsCache.current, ...liveServiceOptions]);
    serviceOptionsCache.current = Array.from(merged).sort();
  }, [list.status, liveServiceOptions]);

  const serviceOptions = useMemo(() => {
    const merged = new Set([...serviceOptionsCache.current, ...liveServiceOptions]);
    return Array.from(merged).sort();
  }, [list.status, liveServiceOptions]);

  useEffect(() => {
    const idFromUrl = readRequestIdFromUrl();
    if (idFromUrl != null) setDetailId(idFromUrl);
  }, []);

  // Row-level click → open detail drawer.
  const openDetail = (id) => {
    setDetailId(id);
    try {
      window.history.replaceState(null, "", buildRemediationHubUrl(id));
    } catch (_e) {
      /* no-op */
    }
  };
  const closeDetail = () => {
    setDetailId(null);
    clearRequestIdFromUrl();
  };

  const showingFromIndex = tokenStack.length * (Number(filters.page_size) || 50) + 1;
  const showingToIndex = showingFromIndex + (list.items?.length || 0) - 1;

  const hasError = list.status === "failed";
  const isLoading = list.status === "loading";
  const showSkeleton = isLoading && (!list.items || list.items.length === 0);
  const isBackgroundRefresh = isLoading && (list.items?.length || 0) > 0;
  const noResults =
    list.status === "succeeded" && (!list.items || list.items.length === 0);

  useEffect(() => {
    if (showSkeleton) setSelectedIds([]);
  }, [showSkeleton]);

  return (
    <Box sx={{ ...remediationHubSx, pb: 4, mb: 2 }}>
      <FilterBar
        filters={filters}
        searchValue={searchInput}
        onChange={handleFilterChange}
        onSearchChange={setSearchInput}
        onSearchSubmit={applySearchNow}
        onReset={handleResetFilters}
        onRefresh={() => reloadCurrent()}
        refreshing={refreshing}
        accountOptions={accountOptions}
        accountsLoading={accountsLoading}
        serviceOptions={serviceOptions}
      />

      {hasError && (
        <Alert severity="error" sx={{ mb: 2 }} action={
          <Button color="inherit" size="small" onClick={reloadCurrent}>Retry</Button>
        }>
          {friendlyErrorMessage(list.error)}
        </Alert>
      )}

      {selectedIds.length > 0 && (
        <Paper elevation={0} sx={{ p: 1.5, mb: 1.5, backgroundColor: "primary.50", border: 1, borderColor: "primary.100" }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {selectedIds.length} selected
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<EventIcon />}
              disabled={bulkActionableSelectedIds.length === 0}
              onClick={handleBulkReschedule}
            >
              Re-schedule
              {bulkActionableSelectedIds.length > 0 && skippedBulkActionCount > 0
                ? ` (${bulkActionableSelectedIds.length})`
                : ""}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              disabled={bulkActionableSelectedIds.length === 0}
              onClick={() => setBulkCancelOpen(true)}
            >
              Cancel active
              {bulkActionableSelectedIds.length > 0 && skippedBulkActionCount > 0
                ? ` (${bulkActionableSelectedIds.length})`
                : ""}
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button size="small" onClick={() => setSelectedIds([])}>Clear selection</Button>
          </Stack>
        </Paper>
      )}

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          borderRadius: "10px",
          borderColor: "divider",
          position: "relative",
          ...(isBackgroundRefresh && {
            "&::after": {
              content: '""',
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(255,255,255,0.55)",
              pointerEvents: "none",
              zIndex: 1,
            },
          }),
        }}
      >
        <Table size="small" sx={{ opacity: isBackgroundRefresh ? 0.45 : 1, transition: "opacity 0.2s" }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f9fafb" }}>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  disabled={showSkeleton || !(list.items?.length > 0)}
                  indeterminate={
                    selectedIds.length > 0 && selectedIds.length < displayItems.length
                  }
                  checked={(displayItems.length || 0) > 0 && selectedIds.length === displayItems.length}
                  onChange={(e) => toggleAll(e.target.checked)}
                />
              </TableCell>
              <SortableHeader field="id" sort={filters.sort} onSort={handleSortColumn} sx={{ width: 72 }}>
                Task
              </SortableHeader>
              <SortableHeader field="check_title" sort={filters.sort} onSort={handleSortColumn}>
                Check
              </SortableHeader>
              <SortableHeader field="resource_arn" sort={filters.sort} onSort={handleSortColumn}>
                Resource
              </SortableHeader>
              <SortableHeader field="account" sort={filters.sort} onSort={handleSortColumn}>
                Account
              </SortableHeader>
              <SortableHeader field="severity" sort={filters.sort} onSort={handleSortColumn}>
                Severity
              </SortableHeader>
              <SortableHeader field="status" sort={filters.sort} onSort={handleSortColumn}>
                Status
              </SortableHeader>
              <SortableHeader field="scheduled_at" sort={filters.sort} onSort={handleSortColumn}>
                Scheduled
              </SortableHeader>
              <SortableHeader field="created_at" sort={filters.sort} onSort={handleSortColumn}>
                Created
              </SortableHeader>
              <SortableHeader field="created_by" sort={filters.sort} onSort={handleSortColumn}>
                Submitted by
              </SortableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {showSkeleton &&
              Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  <TableCell padding="checkbox">
                    <Skeleton variant="circular" width={18} height={18} animation="wave" />
                  </TableCell>
                  <TableCell>
                    <Skeleton animation="wave" height={18} width={48} sx={{ borderRadius: 0.5 }} />
                  </TableCell>
                  <TableCell>
                    <Skeleton animation="wave" height={20} width={`${55 + (i % 3) * 12}%`} />
                  </TableCell>
                  <TableCell>
                    <Skeleton animation="wave" height={16} width={`${70 + (i % 2) * 10}%`} />
                  </TableCell>
                  <TableCell>
                    <Skeleton animation="wave" height={18} width="60%" />
                    <Skeleton animation="wave" height={12} width="40%" sx={{ mt: 0.5 }} />
                  </TableCell>
                  <TableCell>
                    <Skeleton animation="wave" height={22} width={64} sx={{ borderRadius: 1 }} />
                  </TableCell>
                  <TableCell>
                    <Skeleton animation="wave" height={22} width={88} sx={{ borderRadius: 1 }} />
                  </TableCell>
                  <TableCell>
                    <Skeleton animation="wave" height={16} width="50%" />
                  </TableCell>
                  <TableCell>
                    <Skeleton animation="wave" height={16} width="45%" />
                  </TableCell>
                  <TableCell>
                    <Skeleton animation="wave" height={14} width="65%" />
                  </TableCell>
                </TableRow>
              ))}
            {!showSkeleton && displayItems.map((row) => {
              const checked = selectedIds.includes(row.id);
              return (
                <TableRow
                  key={row.id}
                  hover
                  onClick={() => openDetail(row.id)}
                  selected={checked}
                  sx={getRequestRowSx(row, { selected: checked })}
                >
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      size="small"
                      checked={checked}
                      onChange={() => toggleRow(row.id)}
                    />
                  </TableCell>
                  <TableCell sx={{ verticalAlign: "middle" }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                        fontSize: 12,
                        fontWeight: 600,
                        color: "text.secondary",
                        letterSpacing: "0.02em",
                      }}
                    >
                      {formatRequestRef(row.id)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Tooltip title={row.check_id} arrow>
                      <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3 }}>
                        {row.check_title}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280, verticalAlign: "top" }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: 12,
                        lineHeight: 1.4,
                        whiteSpace: "normal",
                        wordBreak: "break-all",
                      }}
                    >
                      {row.resource_arn}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.account_name || "—"}</Typography>
                    <Typography variant="caption" color="text.secondary">{row.account_number}</Typography>
                  </TableCell>
                  <TableCell>
                    <SeverityChip severity={row.severity} />
                  </TableCell>
                  <TableCell>
                    <StatusChip status={row.status} />
                  </TableCell>
                  <TableCell>
                    {row.scheduled_at ? (
                      <Tooltip title={formatAbsolute(row.scheduled_at)} arrow>
                        <span>{formatRelative(row.scheduled_at)}</span>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.secondary">ASAP / —</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={formatAbsolute(row.created_at)} arrow>
                      <span>{formatRelative(row.created_at)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{row.created_by || row.user_email}</Typography>
                  </TableCell>
                </TableRow>
              );
            })}
            {noResults && (
              <TableRow>
                <TableCell colSpan={10} sx={{ borderBottom: "none" }}>
                  <EmptyState
                    variant={Object.keys(filters).some((k) => k !== "sort" && k !== "page_size" && filters[k]) ? "no_match" : "no_data"}
                    onClearFilters={handleResetFilters}
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer / pagination */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1.5, mb: 3 }}>
        <TextField
          select
          size="small"
          label="Page size"
          value={filters.page_size || 50}
          onChange={(e) => handleFilterChange({ page_size: Number(e.target.value) })}
          sx={{ width: 110 }}
        >
          {[25, 50, 100].map((n) => (
            <MenuItem key={n} value={n}>{n}</MenuItem>
          ))}
        </TextField>

        <Box sx={{ flexGrow: 1 }} />

        <Typography variant="caption" color="text.secondary">
          {showSkeleton
            ? "Loading tasks…"
            : list.items?.length
              ? `Showing ${showingFromIndex}–${showingToIndex}`
              : "No results"}
        </Typography>

        <Button size="small" onClick={handleFirstPage} disabled={tokenStack.length === 0}>
          First
        </Button>
        <Button size="small" onClick={handlePrevPage} disabled={tokenStack.length === 0}>
          Prev
        </Button>
        <Button size="small" onClick={handleNextPage} disabled={!list.next_token}>
          Next
        </Button>

        {(refreshing || isBackgroundRefresh) && (
          <Tooltip
            title={
              isBackgroundRefresh
                ? "Updating list…"
                : `Auto-refresh every ${list.next_poll_seconds}s`
            }
            arrow
          >
            <CircularProgress size={14} sx={{ ml: 1 }} />
          </Tooltip>
        )}
      </Stack>

      <RequestDetailDrawer
        open={detailId != null}
        requestId={detailId}
        onClose={closeDetail}
        onRequestRefetch={reloadCurrent}
        onReschedule={(id) => { setRescheduleIds([id]); setRescheduleOpen(true); }}
        onGoToIamTab={onGoToIamTab}
      />

      <RescheduleModal
        open={rescheduleOpen}
        requestIds={rescheduleIds}
        onClose={() => { setRescheduleOpen(false); setRescheduleIds([]); }}
        onDone={reloadCurrent}
      />

      <ConfirmDialog
        open={bulkCancelOpen}
        title={
          bulkActionableSelectedIds.length === 1
            ? "Cancel remediation task"
            : `Cancel ${bulkActionableSelectedIds.length} active tasks`
        }
        description={
          skippedBulkActionCount > 0
            ? `${skippedBulkActionCount} selected task${
                skippedBulkActionCount === 1 ? " is" : "s are"
              } already terminal or in progress and will be skipped.`
            : "Provide an optional reason — your team will see it on each task timeline."
        }
        mode="reason"
        confirmLabel={
          bulkActionableSelectedIds.length === 1
            ? "Cancel task"
            : `Cancel ${bulkActionableSelectedIds.length} tasks`
        }
        confirmColor="error"
        isSubmitting={cancelSubmitting}
        onClose={() => setBulkCancelOpen(false)}
        onConfirm={({ reason }) =>
          dispatch(
            cancelRequests({
              request_ids: bulkActionableSelectedIds,
              reason: reason || null,
            })
          )
        }
      />
    </Box>
  );
};

export default RequestsList;
